<!-- PerfilCombobox.vue -->
<!-- Combobox do campo "Perfil" (tipo de vínculo), reutilizado no onboarding e
     na edição de membro. Digita/filtra, sugere perfis prontos de escola
     (Professor/Aluno/Administrador — ver utils/suggestedMemberProfiles.js) e
     os moldes que o titular já tem. Escolher uma sugestão que ainda não é um
     molde do titular grava o molde na hora, com o preset pronto — texto livre
     não cria nada sozinho, e uma sugestão já adotada não é sobrescrita.

     Construído do zero no estilo do dropdown de Visibilidade em
     EditTextForm.vue (.visibility-trigger/.visibility-menu), e não com
     vue-multiselect: a lib deixava resíduos visuais difíceis de sobrescrever
     e nunca batia certinho com a altura dos outros campos do formulário. -->
<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from "vue";
import * as managedAccountService from "@/services/managedAccountService";
import { SUGGESTED_MEMBER_PROFILES } from "@/utils/suggestedMemberProfiles";

const props = defineProps({
  modelValue: { type: String, default: "" },
  placeholder: { type: String, default: "Ex: filho, aluno, colega…" },
  disabled: { type: Boolean, default: false },
});

// "select" dispara só numa escolha deliberada (clique/Enter num item da
// lista) — nunca ao digitar. Carrega { name, permissions }: `permissions` é
// a grade conhecida daquele perfil (do molde ou da sugestão) já resolvida,
// ou null para um nome livre sem preset — quem escuta decide o que fazer
// (ex.: EditMemberAccountForm.vue sincronizando os toggles da tela).
const emit = defineEmits(["update:modelValue", "molde-saved", "select"]);

const suggestedNames = SUGGESTED_MEMBER_PROFILES.map((p) => p.relationshipType);

// Moldes que o titular já tem (vêm do backend, com a grade de cada um) — os
// já adotados a partir de uma sugestão entram aqui também, para não duplicar
// em "Sugeridos".
const ownMoldes = ref([]);
const ownMoldeNames = computed(() =>
  ownMoldes.value.map((t) => t.relationshipType).filter(Boolean)
);
const isMenuOpen = ref(false);
const dropdownRef = ref(null);
const inputRef = ref(null);

// Filtro da lista — separado do modelValue de propósito: o campo pode já
// vir preenchido (ex.: editando um membro "Aluno"), e abrir o menu deve
// mostrar todos os perfis disponíveis, não só os que batem com o texto que
// já está lá. Só passa a filtrar quando a pessoa digita algo NOVO.
const searchQuery = ref("");

onMounted(async () => {
  document.addEventListener("mousedown", handleClickOutside);
  try {
    const response = await managedAccountService.fetchPermissionTemplates();
    ownMoldes.value = response.data ?? [];
  } catch (e) {
    console.warn(
      "[PerfilCombobox] Falha ao carregar moldes:",
      e.response?.data?.message || e.message
    );
    ownMoldes.value = [];
  }
});

onUnmounted(() => {
  document.removeEventListener("mousedown", handleClickOutside);
});

function currentQuery() {
  return searchQuery.value.trim().toLowerCase();
}

const filteredSuggested = computed(() => {
  const query = currentQuery();
  if (!query) return suggestedNames;
  return suggestedNames.filter((name) => name.toLowerCase().includes(query));
});

const filteredOwn = computed(() => {
  const query = currentQuery();
  const own = ownMoldeNames.value.filter(
    (name) =>
      !suggestedNames.some((s) => s.toLowerCase() === name.toLowerCase())
  );
  if (!query) return own;
  return own.filter((name) => name.toLowerCase().includes(query));
});

// Só oferece "usar este perfil" quando o texto digitado não é (a menos de
// maiúsculas/minúsculas) um nome que já existe em alguma das duas listas.
const canCreate = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return false;
  const all = [...suggestedNames, ...ownMoldeNames.value];
  return !all.some((name) => name.toLowerCase() === query.toLowerCase());
});

// Lista plana na mesma ordem da renderização (sugeridos → seus moldes →
// "usar este perfil") — é nela que a navegação por seta se move.
const flatOptions = computed(() => {
  const list = [];
  for (const name of filteredSuggested.value) {
    list.push({ type: "suggested", name });
  }
  for (const name of filteredOwn.value) {
    list.push({ type: "own", name });
  }
  if (canCreate.value) {
    list.push({ type: "create", name: searchQuery.value.trim() });
  }
  return list;
});

const highlightedIndex = ref(-1);

function scrollHighlightedIntoView() {
  nextTick(() => {
    dropdownRef.value
      ?.querySelector(".perfil-option--highlighted")
      ?.scrollIntoView({ block: "nearest" });
  });
}

// Seta para baixo/cima: abre o menu se estiver fechado; senão move o
// destaque, dando a volta nas pontas. Enter confirma o item destacado.
function moveHighlight(delta) {
  if (props.disabled) return;
  if (!isMenuOpen.value) {
    openMenu();
    return;
  }
  const total = flatOptions.value.length;
  if (!total) return;
  highlightedIndex.value =
    highlightedIndex.value === -1
      ? delta > 0 ? 0 : total - 1
      : (highlightedIndex.value + delta + total) % total;
  scrollHighlightedIntoView();
}

function confirmHighlighted() {
  const option = flatOptions.value[highlightedIndex.value];
  if (!option) return;
  selectOption(option.name);
}

function handleKeydown(event) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    moveHighlight(1);
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    moveHighlight(-1);
  } else if (event.key === "Enter") {
    if (!isMenuOpen.value || highlightedIndex.value === -1) return;
    event.preventDefault();
    confirmHighlighted();
  } else if (event.key === "Escape") {
    isMenuOpen.value = false;
    highlightedIndex.value = -1;
  }
}

function openMenu() {
  if (props.disabled) return;
  // Reabrir some com o filtro de uma digitação anterior — do contrário, um
  // segundo clique no campo já preenchido voltava a mostrar só o que bate
  // com o valor atual, em vez da lista inteira.
  searchQuery.value = "";
  isMenuOpen.value = true;
  highlightedIndex.value = -1;
}

// Clicar no chevron (fora do input) também deve focar e abrir, como o
// gatilho inteiro da Visibilidade reage a um clique em qualquer ponto dele.
function focusInput() {
  inputRef.value?.focus();
  openMenu();
}

function handleInput(event) {
  emit("update:modelValue", event.target.value);
  searchQuery.value = event.target.value;
  highlightedIndex.value = -1;
}

// A grade de um nome conhecido (molde do titular ou sugestão) — usada tanto
// para decidir se uma sugestão já foi adotada quanto para informar quem
// escuta "select" do que se trata aquele perfil, sem precisar esperar um
// fetch. Retorna null para um nome livre sem preset (o "Usar X" da lista).
function resolvePermissionsForName(name) {
  const lower = name.toLowerCase();
  const ownMatch = ownMoldes.value.find(
    (t) => (t.relationshipType || "").toLowerCase() === lower
  );
  if (ownMatch) return ownMatch.permissions ?? null;

  const suggestion = SUGGESTED_MEMBER_PROFILES.find(
    (p) => p.relationshipType.toLowerCase() === lower
  );
  return suggestion?.permissions ?? null;
}

async function selectOption(name) {
  const trimmed = (name || "").trim();
  if (!trimmed) return;

  // "select" ANTES de "update:modelValue" de propósito: quem escuta pode
  // precisar comparar o estado atual contra o PERFIL ANTIGO (ex.: "esse
  // membro já tinha permissão própria?") antes do valor do campo mudar.
  emit("select", { name: trimmed, permissions: resolvePermissionsForName(trimmed) });
  emit("update:modelValue", trimmed);
  searchQuery.value = "";
  isMenuOpen.value = false;
  highlightedIndex.value = -1;

  const alreadyOwn = ownMoldeNames.value.some(
    (n) => n.toLowerCase() === trimmed.toLowerCase()
  );
  if (alreadyOwn) return;

  const suggestion = SUGGESTED_MEMBER_PROFILES.find(
    (p) => p.relationshipType.toLowerCase() === trimmed.toLowerCase()
  );
  // Texto livre (sem corresponder a uma sugestão) não grava molde sozinho —
  // só a escolha explícita de uma sugestão ainda não adotada faz isso.
  if (!suggestion) return;

  try {
    await managedAccountService.upsertPermissionTemplate(
      suggestion.relationshipType,
      suggestion.permissions
    );
    ownMoldes.value = [
      ...ownMoldes.value,
      { relationshipType: suggestion.relationshipType, permissions: suggestion.permissions },
    ];
    emit("molde-saved", suggestion.relationshipType);
  } catch (e) {
    console.warn(
      "[PerfilCombobox] Falha ao gravar molde da sugestão:",
      e.response?.data?.message || e.message
    );
  }
}

function handleClickOutside(event) {
  if (isMenuOpen.value && !event.target.closest(".perfil-dropdown")) {
    isMenuOpen.value = false;
    highlightedIndex.value = -1;
  }
}
</script>

<template>
  <div ref="dropdownRef" class="perfil-dropdown">
    <div class="perfil-trigger" :class="{ active: isMenuOpen, 'perfil-trigger--disabled': disabled }">
      <input
        ref="inputRef"
        :value="modelValue"
        type="text"
        class="perfil-trigger__input"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        @input="handleInput"
        @focus="openMenu"
        @keydown="handleKeydown"
      />
      <i
        class="fas fa-chevron-down perfil-trigger__chevron"
        :class="{ open: isMenuOpen }"
        @mousedown.prevent="focusInput"
      ></i>
    </div>

    <div v-if="isMenuOpen" class="perfil-menu">
      <ul>
        <template v-for="(option, index) in flatOptions" :key="option.type + '-' + option.name">
          <li
            v-if="
              option.type !== 'create' &&
              (index === 0 || flatOptions[index - 1].type !== option.type)
            "
            class="perfil-group-label"
          >
            {{ option.type === "suggested" ? "Sugeridos" : "Seus moldes" }}
          </li>
          <li
            class="perfil-option"
            :class="{
 'perfil-option--create': option.type === 'create',
              'perfil-option--highlighted': index === highlightedIndex,
            }"
            @mousedown.prevent="selectOption(option.name)"
            @mouseenter="highlightedIndex = index"
          >
            {{ option.type === "create" ? `Usar "${option.name}"` : option.name }}
          </li>
        </template>

        <li
          v-if="!flatOptions.length"
          class="perfil-option perfil-option--empty"
        >
          Nenhum perfil encontrado
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.perfil-dropdown {
  position: relative;
}

.perfil-trigger {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  height: 32px;
  padding: 0 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--surface);
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.perfil-trigger:hover {
  border-color: var(--focus-border);
  background-color: #f5f8ff;
}
.perfil-trigger.active {
  border-color: var(--focus-border);
}
.perfil-trigger--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.perfil-trigger--disabled:hover {
  border-color: var(--border-color);
  background-color: var(--surface);
}

.perfil-trigger__input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-dark);
}
/* !important porque o anel de foco padrão do navegador (:focus-visible em
   campo de texto) sobrepõe um outline:none comum aqui — mesmo caso do
   .multiselect__input em main.css, que precisou do mesmo reforço. */
.perfil-trigger__input:focus,
.perfil-trigger__input:focus-visible {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}
.perfil-trigger__input::placeholder {
  font-weight: 400;
}

.perfil-trigger__chevron {
  flex-shrink: 0;
  font-size: 0.6rem;
  color: var(--text-muted);
  opacity: 0.7;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}
.perfil-trigger__chevron.open {
  transform: rotate(180deg);
}

.perfil-menu {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 100;
  /* ~3 opções (com um rótulo de grupo) antes de rolar — items a mais viram
     barra de rolagem em vez de esticar o menu indefinidamente. */
  max-height: 8.5rem;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
}
.perfil-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}

.perfil-group-label {
  padding: 0.35rem 0.7rem 0.15rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-subtle);
}

.perfil-option {
  padding: 0.45rem 0.7rem;
  border-radius: 7px;
  font-size: 0.85rem;
  color: var(--text-dark);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.2s ease-in-out;
}
.perfil-option:hover,
.perfil-option--highlighted {
  background-color: var(--brand-tint);
  color: var(--text-brand);
}
.perfil-option--create {
  color: var(--text-brand);
  font-weight: 600;
}
.perfil-option--empty {
  color: var(--text-subtle);
  cursor: default;
}
.perfil-option--empty:hover {
  background-color: transparent;
  color: var(--text-subtle);
}
</style>
